// 192 -- save the event's current grid as a named match day template.
// Layout only: fields, times, game lengths, sport, age group -- never teams.
// A name clash returns 409 (code: template_name_taken); the dialog then offers
// an explicit overwrite instead of silently replacing.

import { useState } from "react";
import * as api from "@/lib/api";
import { notifySaving, notifySaved } from "@/lib/save-status";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const TEMPLATE_NAME_TAKEN_CODE = "template_name_taken";

export function SaveTemplateDialog({ eventId, matchCount, onClose }: {
  eventId: string;
  matchCount: number;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [needsOverwrite, setNeedsOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    notifySaving();
    try {
      await api.saveEventAsTemplate(eventId, {
        name: name.trim(),
        ...(needsOverwrite ? { overwrite: true } : {}),
      });
      notifySaved();
      onClose();
    } catch (e) {
      if (e instanceof api.ApiError && e.status === 409 && e.body?.code === TEMPLATE_NAME_TAKEN_CODE) {
        setNeedsOverwrite(true);
        setError(null);
      } else {
        setError(e instanceof api.ApiError ? e.message : "Failed to save template");
      }
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      data-dialog="save-template"
    >
      <Card className="max-h-[90vh] w-full max-w-md overflow-y-auto">
        <CardHeader>
          <CardTitle>Save as template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Saves this day&apos;s layout ({matchCount} {matchCount === 1 ? "match" : "matches"}: fields,
            times, game lengths, sports, age groups) for reuse on a future event. Teams are not saved.
          </p>
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          <Input
            data-field="template-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNeedsOverwrite(false);
            }}
            placeholder="e.g. Saturday home rugby day"
            autoFocus
          />

          {needsOverwrite && (
            <p className="text-sm text-destructive" data-field="overwrite-warning">
              A template named <span className="font-medium">{name.trim()}</span> already exists.
              Saving again will replace it.
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" data-action="save-template-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={needsOverwrite ? "destructive" : "default"}
              data-action="save-template-confirm"
              onClick={save}
              disabled={saving || !name.trim()}
            >
              {saving ? "Saving..." : needsOverwrite ? "Overwrite template" : "Save template"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
